import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FieldType } from "../../types/Interface";
import { WSTestingProductPairValue, WorkshopProductPairProperty } from "../../types/WSTestingData";
import { FolderLabel } from '../../subs/FolderLabel';

import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import Grid from '@mui/material/Grid2';
import { groupBy } from "../../Utils";

export const WSTRiFaceAccordion = ({ productPairData }: { productPairData: WSTestingProductPairValue | null }) => {
    const properties = productPairData?.testCaseProperties ?? [];

    return <>{
        productPairData && properties.length > 0 &&
        <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack width={'100%'} direction={'row'} justifyContent={'space-between'}>
                    <FolderLabel labelText={('Server Properties: ') + productPairData.interfaceName} labelVariant='large' noicon={true} />
                </Stack>
            </AccordionSummary>

            <AccordionDetails>
                <WSTRiFaceGroupAccordion workshopProductProps={properties} />
            </AccordionDetails>
        </Accordion>}
    </>
}

export const WSTRiFaceGroupAccordion = ({ workshopProductProps }:
    {
        workshopProductProps: WorkshopProductPairProperty[]
    }) => {
    const ifacePropGroups: { [key: string]: WorkshopProductPairProperty[] } = groupBy(workshopProductProps, ip => ip.group);
    const groupKeys = [...Object.keys(ifacePropGroups)];

    return (workshopProductProps.length === 0) ? <></> : <>
        {groupKeys.map((key, index) => {
            return <SubGroupAccordion key={groupKeys[index]} workshopProductProps={ifacePropGroups[groupKeys[index]]} groupName={groupKeys[index]} />
        }
        )}
    </>
}

export const SubGroupAccordion = ({ workshopProductProps, groupName }:
    {
        workshopProductProps: WorkshopProductPairProperty[], groupName: string
    }) => {

    // some interfaces don't contain any groups
    return (workshopProductProps.length === 0) ? <></> : <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack width={'100%'} direction={'row'} justifyContent={'space-between'}>
                <Typography>{groupName}</Typography>
                {<FolderLabel labelText={''} labelVariant='large' noicon={true} />}
            </Stack>
        </AccordionSummary>

        <AccordionDetails>
            <form>
                <fieldset disabled={true} className={'disabledForm'}>
                    <Grid container rowGap={10}>
                        {workshopProductProps.map(workshopProductProp => {
                            return <Grid key={workshopProductProp.interfacePropertyId} size={{ xs: workshopProductProp.fieldTypeVal === FieldType.Table || workshopProductProp.fieldTypeVal === FieldType.MultiLineBox ? 12 : 4 }} columnGap={5}>
                                {<PropertyInput workshopProductProperty={workshopProductProp} />}
                            </Grid>
                        })}
                    </Grid>
                </fieldset>
            </form>
        </AccordionDetails>
    </Accordion>
}

export const PropertyInput = ({ workshopProductProperty }:
    {
        workshopProductProperty: WorkshopProductPairProperty,
    }): React.ReactElement => {
    let inner: React.ReactElement = <></>;

    const [inputContents, setInputContents] = React.useState<string>('');

    React.useEffect(() => {
        setInputContents(workshopProductProperty?.value ?? '')
    }, [workshopProductProperty]);

    switch (workshopProductProperty.fieldTypeVal) {
        case FieldType.CheckBox:
            inner = <PropertyInputRadioGroup workshopProductProperty={workshopProductProperty} />
            break;
        case FieldType.MultiLineBox:
            console.log('MultiLineTextBox: ', workshopProductProperty.fieldSchemaObject ?? JSON.stringify(workshopProductProperty.fieldSchemaObject));
            inner = <TextField multiline
                label={<Typography color={'black'} variant='body2'>
                    {workshopProductProperty.name}
                </Typography>}
                slotProps={{ inputLabel: { shrink: true }, }}
                maxRows={workshopProductProperty.fieldSchemaObject?.maxRows ?? 0}
                minRows={workshopProductProperty.fieldSchemaObject?.minRows ?? 0}
                value={inputContents}
                onChange={(e) => setInputContents(e.target.value)} />
            break;
        case FieldType.Table:
            inner = workshopProductProperty.fieldSchemaObject ? <EditableTable workshopProductProperty={workshopProductProperty} />
                : <></>
            break;
        case FieldType.TextBox:
            inner = <TextField
                label={<Typography color={'black'} variant='body2'>
                    {workshopProductProperty.name}
            </Typography>}
            defaultValue={workshopProductProperty?.value ?? ''} slotProps={{ inputLabel: { shrink: true } }}></TextField>
            break;
    }

    return <FormControl fullWidth>
        {[inner]}
    </FormControl>
}

export const PropertyInputRadioGroup = ({ workshopProductProperty }:
    {
        workshopProductProperty: WorkshopProductPairProperty,
    }): React.ReactElement => {
    const [value, setValue] = React.useState('');

    React.useEffect(() => {
        setValue(workshopProductProperty?.value ?? '');
    }, [workshopProductProperty]);

    return <FormControl fullWidth>
        <FormLabel id={"prooertyAsRadioGroup" + workshopProductProperty.interfacePropertyId}
            sx={{ color: 'black' }}
            color='info'>
            {workshopProductProperty.name}
        </FormLabel>
        <RadioGroup value={value} aria-labelledby={"prooertyAsRadioGroup" + workshopProductProperty.interfacePropertyId} name="prooertyAsTrueFalse">
            <FormControlLabel value={'-1'} label={'Yes'} control={<Radio />} />
            <FormControlLabel value={'0'} label={'No'} control={<Radio />} />
        </RadioGroup>
    </FormControl>
}

const newEmptyRow = (columnDefinitons: GridColDef[], id: number) => {
    const rowObject: { [key: string]: any } = { id: id };

    columnDefinitons.forEach((columnDefiniton, index) => {
        rowObject[columnDefiniton.field] = '';
    });

    return rowObject;
}

const parseTableRows = (columnDefinitions: GridColDef[], rows?: Array<string[]>) => {
    const rowObjects: { [key: string]: any }[] = [];

    if (!rows || !Array.isArray(rows)) return rowObjects;

    rows.map((row, index) => {
        let rowObject: { [key: string]: any } = newEmptyRow(columnDefinitions, index);

        columnDefinitions.forEach((columnDefinition, index) =>
            rowObject[columnDefinition.field] = row[index]);

        rowObjects.push(rowObject);
        return null;    // map requires a return, but we don't actually need to return anything here
    })

    return rowObjects;
}

const EditableTable = ({ workshopProductProperty }:
    {
        workshopProductProperty: WorkshopProductPairProperty
    }) => {
    const [tableRows, setTableRows] = React.useState<{ [key: string]: any }[]>([]);   // there is no need to initialize this, as it will be set in useEffect

    console.log('json: ', workshopProductProperty?.value);

    const columnDefintions: GridColDef[] =
        (workshopProductProperty && workshopProductProperty.fieldSchemaObject && Array.isArray(workshopProductProperty.fieldSchemaObject.columns)) ? 
        workshopProductProperty.fieldSchemaObject.columns.map((column: string) => {
        return {
            field: column,
            headerName: column,
            flex: 1,
            editable: true
            }
    }) : [];

    // Safely parse stored JSON for the table. If the stored value is invalid JSON, log a warning and use an empty table.
    React.useEffect(() => {
        setTableRows(parseTableRows(columnDefintions, workshopProductProperty.rows ?? []));
    }, [workshopProductProperty])       // columnDefintions changes every render, so including here creates a loop... find cleaner way to break this loop

    return <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Box
            sx={{
                '& .required-theme--header': {
                    color: 'rgba(255, 0, 0, 1)',
                },
                '& .optional-theme--header': {
                    color: 'rgba(0, 0, 0, 1)',
                },
            }}
        >
            <Typography variant="body1" color="error" sx={{ mb: 1 }}>{workshopProductProperty?.jsonErrorValue ? "*Invalid JSON data, please contact a company representative for correction." : ""}</Typography>
            <DataGrid
                columns={columnDefintions}
                rows={tableRows}
                showColumnVerticalBorder
                showCellVerticalBorder
                hideFooter
                onProcessRowUpdateError={(error) => { console.log('ERROR: ', error); }}
                getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'}
                editMode='row' />
        </Box>
    </div>
}
